import { createContext, useCallback, useContext, useState } from 'react';
import { useParams } from 'next/navigation';
import { Transition } from '@headlessui/react';
import { XMarkIcon as CloseIcon } from '@heroicons/react/24/solid';
import Button from 'components/gruenland/atoms/button/Button';
import Image from 'components/gruenland/atoms/image';
import Link from 'components/gruenland/atoms/link';
import Overlay from 'components/gruenland/atoms/overlay';
import BlockScroll from 'components/gruenland/atoms/utils/BlockScroll';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import { productTileImage } from 'lib/wardaImages';
import { Product, Variant } from 'types/entity/product';
import { AddToCartOverlayContextShape, StateProduct } from './types';

const AddToCartOverlayContext = createContext<AddToCartOverlayContextShape>({
  show() {},
  hide() {},
});

const AddToCartOverlayProvider = ({ children }: React.PropsWithChildren) => {
  const { locale } = useParams();

  const { formatMessage } = useFormat({ name: 'gruenland' });

  const [product, setProduct] = useState<StateProduct>();

  const show = useCallback((product: Product, variant: Variant, count: number) => {
    setProduct({ ...product, ...variant, count });
  }, []);

  const hide = useCallback(() => {
    setProduct(undefined);
  }, []);

  const tileImage = productTileImage(product?.images);

  return (
    <>
      {product && <Overlay onClick={hide} />}
      {product && <BlockScroll />}
      <Transition
        show={!!product}
        as="div"
        className="fixed bottom-0 z-50 w-full overflow-hidden rounded-[20px_20px_0_0] bg-white shadow md:bottom-[unset] md:left-1/2 md:top-1/2 md:w-[90%] md:max-w-[650px] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-lg"
        enter="transition md:transition-opacity duration-75"
        enterFrom="opacity-0 translate-y-full"
        enterTo="opacity-100 translate-y-0"
        leave="transition md:transition-opacity duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-full"
      >
        <div onMouseUp={(e) => e.stopPropagation()}>
          <div className="bg-white p-4 md:px-12 md:py-6">
            <h4 className="text-[18px] leading-[27px] lg:text-[22px] lg:leading-[33px]">
              {formatMessage({ id: 'cart.added', defaultMessage: 'Aggiunto al carrello' })}
            </h4>

            <CloseIcon
              className="absolute right-[18px] top-4 size-7 cursor-pointer fill-secondary-black stroke-0 md:top-[16px]"
              onClick={hide}
            />

            <div className="mt-[22px] md:mt-9 lg:mt-[30px]">
              <div className="flex items-center gap-6">
                <div className="shrink-0">
                  {tileImage && (
                    <Image
                      width={135}
                      height={150}
                      src={tileImage}
                      style={{ objectFit: 'contain' }}
                      alt={product?.name ?? ''}
                    />
                  )}
                </div>
                <div className="flex grow items-start justify-between overflow-hidden">
                  <div className="max-w-full overflow-hidden">
                    <span className="block max-w-full truncate text-[12px] uppercase md:text-[14px]">
                      {product?.name}
                    </span>
                    <span className="mt-2 block text-[12px] font-medium md:hidden">
                      {CurrencyHelpers.formatForCurrency(product?.discountedPrice ?? product?.price ?? {}, locale)}
                    </span>
                    <span className="mt-3 block text-[14px] text-secondary-black">x {product?.count}</span>
                  </div>
                  <span className="hidden text-[14px] font-medium md:block">
                    {CurrencyHelpers.formatForCurrency(product?.discountedPrice ?? product?.price ?? {}, locale)}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-[18px] flex w-full flex-col gap-4 md:mt-9 md:flex-row-reverse">
              <Link link="/cart" onClick={hide} className="md:flex-1">
                <Button className="w-full" variant="buttonPrimaryGreen">
                  {formatMessage({ id: 'cart.go', defaultMessage: 'Vai al carrello' })}
                </Button>
              </Link>
              <div onClick={hide} className="md:flex-1">
                <Button className="w-full" variant="buttonSecondary">
                  {formatMessage({ id: 'cart.continueShopping', defaultMessage: 'Continua lo shopping' })}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
      <AddToCartOverlayContext.Provider value={{ show, hide }}>{children}</AddToCartOverlayContext.Provider>
    </>
  );
};

export default AddToCartOverlayProvider;

export const useAddToCartOverlay = () => useContext(AddToCartOverlayContext);
